import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ReviewsSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const reviews = [
    {
      text: "AnyParser's advanced multimodal AI delivers where other approaches fail, quite this fusion of sight and language.",
      author: "Jon Conradt",
      role: "Principal Scientist - AWS",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
    },
    {
      text: "As an AI educator, I seek SOTA solutions for my ML practitioner students. AnyParser enhances retrieval accuracy in document parsing while balancing security, cost, and efficiency—an innovative tool for any pipeline!",
      author: "Cass",
      role: "Senior Scientist - AWS", 
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face"
    },
    {
      text: "I am impressed by AnyParser's innovation in the space of AI and LLM, including the novel methodologies of synthetic data generation, retrieval model fine-tuning in RAG, and their open-source products out of those innovations.",
      author: "Felix Bai",
      role: "Sr. Solution Architect - AWS",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
    },
    {
      text: "I have validated the quality of AnyParser goes far beyond traditional OCR tools like Langchain / Unstructured. Looking forward to using this in our future projects.",
      author: "Steve Cooper",
      role: "Cofounder - ai ticker chat",
      avatar: "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face"
    },
    {
      text: "We had tried all the pdf extraction tools and AnyParser gave us the most accurate results. The AI integration has transformed our workflow completely.",
      author: "Richard Song",
      role: "CEO - Epsilla",
      avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face"
    },
    {
      text: "AnyParser outperformed 10+ other parsers in our benchmarks, delivering top-tier accuracy with the fastest multi-model LLM solution—all while maintaining exceptional performance.",
      author: "Ethan Zheng",
      role: "CTO - Jobright",
      avatar: "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face"
    }
  ];

  const itemsToShow = 4;
  const totalSlides = Math.ceil(reviews.length / itemsToShow);

  // Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Reviews</span>
          </h2>
        </motion.div>

        {/* Reviews Slider Container */}
        <div className="relative max-w-7xl mx-auto">
          <div className="overflow-hidden">
            {/* Reviews Track */}
            <motion.div
              className="flex transition-transform duration-700 ease-in-out"
              animate={{ x: `-${currentIndex * 100}%` }}
            >
              {/* Create slides */}
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div
                  key={slideIndex}
                  className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                >
                  {reviews
                    .slice(slideIndex * itemsToShow, (slideIndex + 1) * itemsToShow)
                    .map((review, index) => (
                      <motion.div
                        key={`${slideIndex}-${index}`}
                        className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 h-80 flex flex-col justify-between hover:border-gray-700 transition-all duration-300"
                        whileHover={{ y: -5, scale: 1.02 }}
                      >
                        {/* Review Text */}
                        <div className="flex-1">
                          <p className="text-gray-300 text-sm leading-relaxed">
                            "{review.text}"
                          </p>
                        </div>

                        {/* Author Info */}
                        <div className="flex items-center space-x-3 mt-4">
                          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-700">
                            <img
                              src={review.avatar}
                              alt={review.author}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = `https://ui-avatars.com/api/?name=${review.author}&size=100&background=1f2937&color=ffffff&format=png`;
                              }}
                            />
                          </div>
                          <div>
                            <div className="font-semibold text-white text-sm">{review.author}</div>
                            <div className="text-gray-400 text-xs">{review.role}</div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-blue-400' 
                    : 'w-2 bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Floating Animation Elements */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/40 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-blue-300/50 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default ReviewsSection;